import { defineComponent as _defineComponent } from 'vue'
import Dialog from "@/components/vuetify-wrappers/Dialog.vue";
import NjBtn from "@/components/vuetify-wrappers/NjButton.vue";
import { ApiService, SnackbarService } from "@/services";
import { useBriefcaseStore } from "@/pinia/briefcase";
import {
  BriefcaseFolder,
  BriefcaseFolderResponse,
  VignetteRoutes,
} from "@/types";
import { computed, getCurrentInstance, nextTick, ref, watch } from "vue";
import { useRoute } from "vue-router/composables";
import { storeToRefs } from "pinia";

interface Props {
  isOpen: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BriefcaseMassAddDialog',
  props: {
    isOpen: { type: Boolean, default: false }
  },
  emits: ["dialog-changed"],
  setup(__props: any, { emit: emits }) {

const props = __props as { isOpen: boolean };



const isOpen = computed({
  get: () => props.isOpen,
  set: (value: boolean) => emits("dialog-changed", value),
});
watch(isOpen, (value) => {
  if (!value) {
    initializeSelectedFolders();
    resetNewFolderInput();
  }
});

const route = useRoute();

const instance = getCurrentInstance();

const briefcaseStore = useBriefcaseStore();
const { itemIds } = storeToRefs(briefcaseStore);
const currentFolder = computed({
  get: () => briefcaseStore.folders[briefcaseStore.meta.currentFolderId],
  set: (folder: BriefcaseFolder) => {
    briefcaseStore.meta.currentFolderId = folder.id;
  },
});
watch(currentFolder, (newValue, oldValue) => {
  if (displayAddFolderDialog.value) {
    return;
  } else {
    nextTick(() => {
      // Need to call this within nextTick, otherwise Vuetify won't update select back to original value in same cycle
      // Switch back to previous value to in current folder
      currentFolder.value = oldValue;
    });
    displayAddFolderDialog.value = true;
  }
});
const briefcaseFolders = computed(() => {
  return Object.values(briefcaseStore.folders);
});

const oldFolders = ref<number[]>([]);

const selectedFolders = ref<number[]>([]);
const initializeSelectedFolders = () => {
  // Using a set to easily handle duplicates.
  selectedFolders.value = [];

  for (const briefcase of briefcaseFolders.value) {
    const briefcaseProfileIds: number[] = [];
    for (const item of briefcase.items) {
      briefcaseProfileIds.push(item.id);
    }

    oldFolders.value = [...selectedFolders.value];
  }
};

const displayAddFolderDialog = ref(false);
const isLoading = ref(false);
const newFolderInput = ref<HTMLFormElement | null>(null);

const folderToBeAddedName = ref("");
const newFolderNameValidated = computed(() => {
  return (
    !!folderToBeAddedName.value.trim() &&
    folderToBeAddedName.value.trim().length <= 100 &&
    briefcaseStore.isNewFolderUnique(folderToBeAddedName.value).value
  );
});
const routeToDocTypeMapper: { [key: string]: string } = {
  [VignetteRoutes.SearchCongressional]: "congressional",
  [VignetteRoutes.SearchFederal]: "federal",
  [VignetteRoutes.SearchState]: "state",
  [VignetteRoutes.SearchUniversal]: "universal",
};
const addSearchResults = async () => {
  try {
    // Create a new folder if need be.
    if (newFolderNameValidated.value) {
      await addNewFolder();
    }

    isLoading.value = true;
    const docType = routeToDocTypeMapper[route.name as string];
    const items = await ApiService.addAllSearchResults(
      currentFolder.value.id,
      docType
    );
    itemIds.value.push(...items);
    ApiService.refreshBriefcase(true);

    SnackbarService.displaySnackbar(
      `Contents of search have been added to your folder.`,
      "primary"
    );

    resetNewFolderInput();
    instance?.proxy?.$forceUpdate();
    isOpen.value = false;
  } catch (e: unknown) {
    SnackbarService.displaySnackbar(e as string, "error");
  } finally {
    isLoading.value = false;
  }
};

const addNewFolder = async () => {
  const validatedName = folderToBeAddedName.value.trim();
  const response: BriefcaseFolderResponse =
    await ApiService.postBriefcaseFolderAdd(validatedName);

  if (response.statusCode === 200 && response.folder_id) {
    SnackbarService.displaySnackbar(
      `Folder "${validatedName}" created.`,
      "primary"
    );
    folderToBeAddedName.value = "";
    displayAddFolderDialog.value = false;
    currentFolder.value =
      briefcaseFolders.value.find((item) => item.id === response.folder_id) ||
      currentFolder.value;
  } else {
    SnackbarService.displaySnackbar("An error occurred. Please try again.");
  }
};

const resetNewFolderInput = () => {
  folderToBeAddedName.value = "";
  newFolderInput.value?.resetValidation();
};

return { __sfc: true,emits, props, isOpen, route, instance, briefcaseStore, itemIds, currentFolder, briefcaseFolders, oldFolders, selectedFolders, initializeSelectedFolders, displayAddFolderDialog, isLoading, newFolderInput, folderToBeAddedName, newFolderNameValidated, routeToDocTypeMapper, addSearchResults, addNewFolder, resetNewFolderInput, Dialog, NjBtn }
}

})